import React from "react";

/** Italian Amalfi-style lemon with leaf. */
export const Lemon = ({ className = "" }) => (
  <svg
    aria-hidden="true"
    viewBox="0 0 80 80"
    fill="none"
    className={className}
  >
    {/* Leaf */}
    <path
      d="M30 22 C 22 14, 14 18, 18 28 C 22 30, 28 28, 30 22 Z"
      fill="#7E9B6A"
      opacity="0.9"
    />
    <path d="M22 22 L 28 26" stroke="#56753E" strokeWidth="0.7" />
    {/* Lemon body */}
    <ellipse
      cx="44"
      cy="46"
      rx="22"
      ry="20"
      fill="currentColor"
      transform="rotate(-12 44 46)"
    />
    {/* Highlight */}
    <ellipse
      cx="36"
      cy="38"
      rx="6"
      ry="4"
      fill="white"
      opacity="0.55"
      transform="rotate(-25 36 38)"
    />
    {/* Tip */}
    <path
      d="M64 50 C 68 50, 70 52, 68 56"
      stroke="currentColor"
      strokeWidth="2"
      fill="none"
      transform="rotate(-12 64 50)"
    />
    {/* Texture dots */}
    <g opacity="0.35" fill="#9A6E14">
      <circle cx="40" cy="44" r="0.8" />
      <circle cx="48" cy="40" r="0.8" />
      <circle cx="50" cy="50" r="0.8" />
      <circle cx="42" cy="54" r="0.8" />
      <circle cx="56" cy="48" r="0.8" />
    </g>
  </svg>
);

/** Olive branch with leaves and olives — slim & elegant. */
export const OliveBranch = ({ className = "" }) => (
  <svg
    aria-hidden="true"
    viewBox="0 0 200 60"
    fill="none"
    className={className}
  >
    <path
      d="M5 30 C 60 28, 120 32, 195 30"
      stroke="currentColor"
      strokeWidth="0.8"
    />
    {[20, 50, 80, 110, 140, 170].map((cx, i) => (
      <g key={i}>
        <path
          d={`M${cx} 30 C ${cx - 8} ${i % 2 === 0 ? 18 : 42}, ${cx + 4} ${
            i % 2 === 0 ? 16 : 44
          }, ${cx + 12} ${i % 2 === 0 ? 24 : 36} Z`}
          fill="currentColor"
          opacity="0.65"
        />
      </g>
    ))}
    <circle cx="35" cy="30" r="3" fill="#5A6F3E" opacity="0.85" />
    <circle cx="95" cy="30" r="3" fill="#5A6F3E" opacity="0.85" />
    <circle cx="155" cy="30" r="3" fill="#5A6F3E" opacity="0.85" />
  </svg>
);

/** Stylised pizza slice. */
export const PizzaSlice = ({ className = "" }) => (
  <svg
    aria-hidden="true"
    viewBox="0 0 64 64"
    fill="none"
    className={className}
  >
    <path
      d="M6 56 L 58 56 L 32 8 Z"
      fill="#F4D35E"
      stroke="#C97B5E"
      strokeWidth="1.2"
      strokeLinejoin="round"
    />
    <path
      d="M6 56 L 58 56"
      stroke="#C97B5E"
      strokeWidth="3"
      strokeLinecap="round"
    />
    <circle cx="22" cy="42" r="3" fill="#C9583E" />
    <circle cx="38" cy="38" r="3" fill="#C9583E" />
    <circle cx="32" cy="48" r="2.5" fill="#C9583E" />
    <circle cx="28" cy="32" r="2" fill="#7E9B6A" />
    <circle cx="40" cy="48" r="2" fill="#7E9B6A" />
    <circle cx="34" cy="22" r="2" fill="#7E9B6A" />
    <circle cx="22" cy="50" r="1.8" fill="white" opacity="0.85" />
    <circle cx="42" cy="44" r="1.8" fill="white" opacity="0.85" />
  </svg>
);

/** Citrus garland — decorative full-width strip. */
export const CitrusGarland = ({ className = "" }) => (
  <svg
    aria-hidden="true"
    viewBox="0 0 600 40"
    fill="none"
    className={className}
  >
    <path
      d="M0 20 C 100 6, 200 34, 300 20 S 500 6, 600 20"
      stroke="#7E9B6A"
      strokeWidth="0.9"
    />
    {[80, 200, 320, 440, 560].map((cx, i) => (
      <g key={i}>
        <ellipse
          cx={cx}
          cy={i % 2 === 0 ? 14 : 26}
          rx="5"
          ry="4"
          fill="#F4D35E"
        />
        <path
          d={`M${cx - 6} ${i % 2 === 0 ? 12 : 24} L ${cx - 10} ${
            i % 2 === 0 ? 8 : 28
          }`}
          stroke="#7E9B6A"
          strokeWidth="0.8"
        />
      </g>
    ))}
  </svg>
);

/** A branch with hanging lemons + leaves – the iconic Amalfi look. */
export const LemonBranch = ({ className = "", flip = false }) => (
  <svg
    aria-hidden="true"
    viewBox="0 0 360 280"
    fill="none"
    className={className}
    style={flip ? { transform: "scaleX(-1)" } : undefined}
  >
    <path
      d="M-10 20 C 80 10, 160 35, 230 70 C 280 95, 320 130, 350 175"
      stroke="#7C5A2E"
      strokeWidth="2.2"
      fill="none"
    />
    <path d="M70 20 C 80 40, 78 60, 70 80" stroke="#7C5A2E" strokeWidth="1.4" fill="none" />
    <path d="M150 32 C 145 60, 158 80, 175 100" stroke="#7C5A2E" strokeWidth="1.4" fill="none" />
    <path d="M240 80 C 236 110, 245 130, 264 145" stroke="#7C5A2E" strokeWidth="1.4" fill="none" />
    <path d="M310 132 C 308 158, 316 180, 332 198" stroke="#7C5A2E" strokeWidth="1.4" fill="none" />

    {[
      { cx: 40, cy: 8, rx: 22, ry: 9, rot: -15 },
      { cx: 110, cy: 18, rx: 24, ry: 9, rot: -8 },
      { cx: 180, cy: 50, rx: 20, ry: 8, rot: 14 },
      { cx: 260, cy: 95, rx: 22, ry: 9, rot: 26 },
      { cx: 330, cy: 160, rx: 22, ry: 9, rot: 38 },
      { cx: 90, cy: 70, rx: 18, ry: 7, rot: 60 },
      { cx: 200, cy: 110, rx: 18, ry: 7, rot: 70 },
    ].map((l, i) => (
      <g key={`leaf-${i}`} transform={`rotate(${l.rot} ${l.cx} ${l.cy})`}>
        <ellipse cx={l.cx} cy={l.cy} rx={l.rx} ry={l.ry} fill="#7E9B6A" opacity="0.95" />
        <path
          d={`M${l.cx - l.rx} ${l.cy} L ${l.cx + l.rx} ${l.cy}`}
          stroke="#56753E"
          strokeWidth="0.6"
        />
      </g>
    ))}

    {[
      { cx: 78, cy: 78, r: 14 },
      { cx: 178, cy: 102, r: 16 },
      { cx: 268, cy: 150, r: 15 },
      { cx: 332, cy: 200, r: 17 },
      { cx: 130, cy: 60, r: 12 },
    ].map((l, i) => (
      <g key={`lemon-${i}`}>
        <ellipse
          cx={l.cx}
          cy={l.cy}
          rx={l.r * 1.05}
          ry={l.r * 0.95}
          fill="#F1C04A"
          transform={`rotate(-12 ${l.cx} ${l.cy})`}
        />
        <ellipse
          cx={l.cx - l.r * 0.32}
          cy={l.cy - l.r * 0.32}
          rx={l.r * 0.32}
          ry={l.r * 0.2}
          fill="white"
          opacity="0.55"
          transform={`rotate(-25 ${l.cx} ${l.cy})`}
        />
        <path
          d={`M${l.cx + l.r} ${l.cy + 2} c 4 0, 6 2, 4 6`}
          stroke="#C7973A"
          strokeWidth="1.4"
          fill="none"
          transform={`rotate(-12 ${l.cx} ${l.cy})`}
        />
      </g>
    ))}
  </svg>
);

/** Soft rose blossom – curved organic petals. */
export const RoseBlossom = ({ className = "" }) => (
  <svg
    aria-hidden="true"
    viewBox="0 0 80 80"
    fill="none"
    className={className}
  >
    {/* Outer petals */}
    <g>
      <path
        d="M40 8 C 56 14, 60 32, 50 42 C 38 38, 30 24, 40 8 Z"
        fill="currentColor"
        opacity="0.85"
      />
      <path
        d="M72 30 C 70 48, 54 56, 44 50 C 46 38, 58 26, 72 30 Z"
        fill="currentColor"
        opacity="0.78"
      />
      <path
        d="M62 70 C 46 74, 30 64, 32 50 C 44 50, 58 58, 62 70 Z"
        fill="currentColor"
        opacity="0.82"
      />
      <path
        d="M14 56 C 8 40, 18 26, 32 30 C 30 42, 22 54, 14 56 Z"
        fill="currentColor"
        opacity="0.78"
      />
      <path
        d="M22 18 C 36 14, 48 24, 42 36 C 32 36, 22 28, 22 18 Z"
        fill="currentColor"
        opacity="0.82"
      />
    </g>
    {/* Inner bud */}
    <path
      d="M40 32 C 48 34, 50 44, 44 48 C 36 46, 34 38, 40 32 Z"
      fill="currentColor"
      opacity="0.95"
    />
    <circle cx="42" cy="42" r="3" fill="white" opacity="0.45" />
  </svg>
);

/** Single soft rose petal — for floating particles. */
export const RosePetal = ({ className = "" }) => (
  <svg
    aria-hidden="true"
    viewBox="0 0 30 30"
    fill="none"
    className={className}
  >
    <path
      d="M15 3 C 24 8, 27 18, 18 26 C 11 22, 5 14, 15 3 Z"
      fill="currentColor"
      opacity="0.9"
    />
  </svg>
);

/** A soft sun-glow circle — Amalfi summer haze. */
export const SunGlow = ({ className = "" }) => (
  <svg
    aria-hidden="true"
    viewBox="0 0 400 400"
    fill="none"
    className={className}
  >
    <defs>
      <radialGradient id="sun-grad" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#FFE9B0" stopOpacity="0.95" />
        <stop offset="40%" stopColor="#FFD37D" stopOpacity="0.55" />
        <stop offset="75%" stopColor="#F4A664" stopOpacity="0.18" />
        <stop offset="100%" stopColor="#F4A664" stopOpacity="0" />
      </radialGradient>
    </defs>
    <circle cx="200" cy="200" r="200" fill="url(#sun-grad)" />
  </svg>
);

/** Bright Mediterranean sun with subtle rays and warm halo. */
export const SunWithRays = ({ className = "" }) => (
  <svg
    aria-hidden="true"
    viewBox="0 0 600 600"
    fill="none"
    className={className}
  >
    <defs>
      <radialGradient id="sun-core" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#FFEAA1" stopOpacity="1" />
        <stop offset="22%" stopColor="#FFD262" stopOpacity="0.9" />
        <stop offset="55%" stopColor="#F2B062" stopOpacity="0.45" />
        <stop offset="78%" stopColor="#EE8B5A" stopOpacity="0.18" />
        <stop offset="100%" stopColor="#EE8B5A" stopOpacity="0" />
      </radialGradient>
      <radialGradient id="sun-disc" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#FFF4D2" stopOpacity="1" />
        <stop offset="60%" stopColor="#FFD568" stopOpacity="1" />
        <stop offset="100%" stopColor="#F2B14E" stopOpacity="0.85" />
      </radialGradient>
    </defs>
    {/* Outer halo */}
    <circle cx="300" cy="300" r="300" fill="url(#sun-core)" />
    {/* Rays */}
    <g
      stroke="#FFD568"
      strokeWidth="1.4"
      strokeLinecap="round"
      opacity="0.55"
    >
      {Array.from({ length: 24 }).map((_, i) => {
        const a = (i / 24) * Math.PI * 2;
        const r1 = 145;
        const r2 = i % 2 === 0 ? 220 : 200;
        const x1 = 300 + Math.cos(a) * r1;
        const y1 = 300 + Math.sin(a) * r1;
        const x2 = 300 + Math.cos(a) * r2;
        const y2 = 300 + Math.sin(a) * r2;
        return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} />;
      })}
    </g>
    {/* Sun disc */}
    <circle cx="300" cy="300" r="115" fill="url(#sun-disc)" />
  </svg>
);

/** Horizontal sea waves – horizon line of the Mediterranean. */
export const SeaWaves = ({ className = "" }) => (
  <svg
    aria-hidden="true"
    viewBox="0 0 1600 240"
    fill="none"
    preserveAspectRatio="none"
    className={className}
  >
    <defs>
      <linearGradient id="wave-fade" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#7DB1C9" stopOpacity="0" />
        <stop offset="50%" stopColor="#7DB1C9" stopOpacity="0.5" />
        <stop offset="100%" stopColor="#7DB1C9" stopOpacity="0" />
      </linearGradient>
    </defs>
    <g stroke="url(#wave-fade)" strokeWidth="1.4" fill="none" strokeLinecap="round">
      <path d="M0 40 C 200 20, 400 60, 600 40 S 1000 20, 1200 40 S 1500 60, 1600 40" />
      <path d="M0 80 C 200 60, 400 100, 600 80 S 1000 60, 1200 80 S 1500 100, 1600 80" opacity="0.85" />
      <path d="M0 120 C 200 100, 400 140, 600 120 S 1000 100, 1200 120 S 1500 140, 1600 120" opacity="0.7" />
      <path d="M0 160 C 200 140, 400 180, 600 160 S 1000 140, 1200 160 S 1500 180, 1600 160" opacity="0.55" />
      <path d="M0 200 C 200 180, 400 220, 600 200 S 1000 180, 1200 200 S 1500 220, 1600 200" opacity="0.4" />
    </g>
    {/* Sun shimmer reflection */}
    <g stroke="#F4C95D" strokeWidth="1.6" strokeLinecap="round" opacity="0.5">
      <path d="M1180 30 L 1240 50" />
      <path d="M1170 70 L 1250 90" />
      <path d="M1160 110 L 1260 130" />
      <path d="M1170 150 L 1250 170" />
      <path d="M1180 190 L 1240 210" />
    </g>
  </svg>
);
